import React from 'react';
import { Outlet } from 'react-router-dom';
import { PageControl, Slider } from '../common';
import AuthBg from '../../assets/images/auth_bg.svg';
import { sliderData } from '../../lib/constants';
import { useBranding, PoweredByFooter, SponsorLine } from '../../contexts/BrandingContext';

interface AuthBannerLayoutProps {
  children?: React.ReactNode;
}

const AuthBannerLayout = ({ children }: AuthBannerLayoutProps) => {
  // V1.5.M10 B-P1: the auth surface is the first thing an invited attendee
  // sees — brand logo + name, and (when the client supplies one) the banner
  // image replacing the default auth background.
  const { logo, logoDark, name: brandName, bannerUrl, isDefault } = useBranding();
  return (
    <div className='h-screen lg:flex items-center justify-center lg:py-12 py-8 w-full max-w-[76.5rem] mx-auto'>
      <div className='relative lg:w-1/2 hidden lg:block h-full rounded-[1.875rem] overflow-hidden'>
        <div className='h-full w-full text-white relative p-12 flex flex-col items-center justify-between text-center [&>img]:z-[1] [&>*]:z-[2]'>
          <div>
            <img
              src={logo}
              alt={brandName}
              className='w-[18.75rem] h-auto'
            />
            <SponsorLine className='text-center text-xs text-white/80 mt-2 select-none' />
          </div>
          {/* The value-prop carousel below is 2Connect's own copy, so only render
              it on the 2Connect default brand. Client (white-label) sign-ins omit
              it, so a branded event never carries generic 2Connect marketing.
              Per-event custom slides are a follow-up branding field. */}
          {isDefault && (
          <Slider
            sliderWrapperClass='items-end'
            wrapperClass='!h-fit'
            pagination={true}
            autoplay={true}
            slideClass='!h-fit'
            loop={true}
            fade={true}
            customPagination={({
              currentIndex,
              total,
              goToSlide,
            }: {
              currentIndex: number;
              total: number;
              goToSlide: (index: number) => void;
            }) => {
              return (
                <div className='w-full flex justify-center absolute w-full h-fit left-0 bottom-0 z-3'>
                  <PageControl
                    totalPages={total}
                    currentPage={currentIndex + 1}
                    onPageChange={(page: number) => goToSlide(page - 1)}
                  />
                </div>
              );
            }}
          >
            {sliderData.map((item, index) => {
              return (
                <div key={index} className='mb-12'>
                  <h2 className='!text-[2.5rem] !leading-[3rem] !font-heading !font-medium text-center mb-1'>
                    {item.title}
                  </h2>
                  <p className='mb-4'>{item.description}</p>
                </div>
              );
            })}
          </Slider>
          )}
          <img
            src={bannerUrl ?? AuthBg}
            alt='auth-bg'
            className='absolute top-0 left-0 w-full h-full object-cover'
          />
        </div>
      </div>
      <div className='lg:hidden flex justify-center items-center pb-8 border-b border-gray-200'>
        <img
          src={logoDark}
          alt={brandName}
          className='max-w-[7.0625rem] w-full h-auto'
        />
      </div>
      <div className='lg:w-1/2 w-full px-4 lg:px-0 lg:min-h-auto min-h-[calc(100dvh-5.1875rem)] lg:block flex flex-col justify-center'>
        {children || <Outlet />}
        <PoweredByFooter />
      </div>
    </div>
  );
};

export default AuthBannerLayout;
